import React, { FunctionComponent, useEffect, useState } from 'react';
import { RootState } from '../../app/store';
import { ConnectedProps, connect } from 'react-redux';
import { useIntl } from 'react-intl';
import messages from './messages';
import { StyledTable, Wrap } from '../../assets/styledAssets/grid';
import {
  accentColorPrim,
  accentColorSecond,
  fontColorSecond,
  red,
  white,
} from '../../assets/styledAssets/vars';
import Button from '../../components/Button/Button.component';
import {
  DeleteIcon,
  EditIcon,
  PlusIcon,
} from '../../components/Icons/Icons.component';
import { GridColumn } from '@progress/kendo-react-grid';
import { DropdownResponse } from '../../domains/app';
import {
  createTestType,
  deleteTestType,
  fetchUserExercise,
  updateTestType,
} from '../../service/Results.service';
import { StyledTD } from '../Questionnaires/QuestionnairesList/styles';
import DialogWrap from '../../components/DiaogWrap/DiaogWrap.component';
import TestTypeDetails from './components/TestTypeDetails/TestTypeDetails.component';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog.component';

const connector = connect((state: RootState) => ({}), {});

type PropsFromRedux = ConnectedProps<typeof connector>;

const TestTypes: FunctionComponent<PropsFromRedux> = () => {
  const intl = useIntl();
  const [listTypes, setListTypes] = useState<DropdownResponse[]>([]);
  const [showAddTestType, setShowAddTestType] = useState<boolean>();
  const [activeTestType, setActiveTestType] = useState<DropdownResponse>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [confirmationHandler, setConfirmationHandler] = useState<() => void>();

  useEffect(() => {
    fetchUserExercise([]).then((res) => setListTypes(res));
  }, []);

  const removeTestType = (id: string) => {
    setShowConfirmation(true);
    setConfirmationText(intl.formatMessage(messages.deleteGroup));
    setConfirmationHandler(() => {
      return () => {
        deleteTestType(id).then(() => {
          setListTypes(listTypes.filter((t) => t.id !== id));
        });
      };
    });
  };

  const addTestType = (name: string) => {
    if (activeTestType?.id) {
      updateTestType(activeTestType.id, name).then((res) => {
        setListTypes((list) => {
          (
            list.find((t) => t.id === activeTestType.id) ||
            ({} as DropdownResponse)
          ).name = name;
          return [...list];
        });
        setShowAddTestType(false);
        setActiveTestType(undefined);
      });
    } else {
      createTestType(name).then((res) => {
        setListTypes(listTypes.concat([res]));
        setShowAddTestType(false);
      });
    }
  };

  return (
    <Wrap
      flexDir={'column'}
      flexWrap={'nowrap'}
      overflow={['auto', 'unset']}
      width={'100%'}
    >
      <Wrap flexDir={'column'} width={'100%'}>
        <Wrap
          flexDir={['column', 'row']}
          alignItems={['flex-start', 'center']}
          justifyCont={['flex-start', 'space-between']}
          width={'100%'}
        >
          <Wrap
            alignItems={['flex-start', 'flex-end']}
            flexDir={['column', 'row']}
          >
            <Wrap
              fs={'3.2rem'}
              fw={'bold'}
              color={'#000'}
              alignItems={'flex-end'}
              borders={[
                [{ side: 'right', color: 'transparent' }],
                [{ side: 'right', color: accentColorSecond }],
              ]}
              padding={['0', '0 3rem 0 0']}
              margin={['0 0 1rem 0', '0 3rem 0 0']}
            >
              {intl.formatMessage(messages.title)}
            </Wrap>
            <Wrap
              fs={'1.8rem'}
              color={fontColorSecond}
              alignItems={'flex-end'}
              padding={'0 0 .5rem 0'}
            >
              {listTypes.length} {intl.formatMessage(messages.total)}
            </Wrap>
          </Wrap>
          <Wrap
            alignItems={'center'}
            margin={['2rem 0', '0']}
            justifyCont={['flex-start', 'flex-end']}
          >
            <Button primary onClick={() => setShowAddTestType(true)}>
              <PlusIcon color={white} width={'1.4rem'} />
              {intl.formatMessage(messages.addTestType)}
            </Button>
          </Wrap>
        </Wrap>
      </Wrap>
      <Wrap
        grow={'1'}
        overflow={'hidden'}
        width={'100%'}
        height={'100%'}
        hiddenXS
      >
        <StyledTable style={{ height: '100%', width: '100%' }} data={listTypes}>
          <GridColumn
            field="name"
            width="400px"
            title={intl.formatMessage(messages.name)}
          />
          <GridColumn field="id" title={intl.formatMessage(messages.id)} />
          <GridColumn
            width={'90px'}
            cell={(props) => (
              <StyledTD>
                <Wrap>
                  <Wrap>
                    <Button
                      icon
                      transparent
                      onClick={(e: any) => {
                        e.preventDefault();
                        setActiveTestType(props.dataItem);
                        setShowAddTestType(true);
                      }}
                    >
                      <EditIcon color={accentColorPrim} width={'2rem'} />
                    </Button>
                  </Wrap>
                  <Wrap margin={'0 0 0 1rem'} style={{ display: 'none' }}>
                    <Button
                      icon
                      transparent
                      onClick={(e: any) => {
                        e.preventDefault();
                        removeTestType(props.dataItem.id);
                      }}
                    >
                      <DeleteIcon color={red} width={'2rem'} />
                    </Button>
                  </Wrap>
                </Wrap>
              </StyledTD>
            )}
          />
        </StyledTable>
      </Wrap>

      <Wrap
        width={'100%'}
        flexDir={'column'}
        overflow={'auto'}
        flexWrap={'nowrap'}
        hiddenSM
      >
        <Wrap width={'100%'}>
          {listTypes.map((item, index) => (
            <Wrap
              margin={'0 0 2rem 0'}
              width={'100%'}
              padding={'3rem'}
              br={'1.2rem'}
              bgColor={white}
              key={`card-${index}`}
              flexDir={'column'}
            >
              <Wrap grow={'1'} alignItems={'center'} justifyCont={'space-between'} width={'100%'}>
                <Wrap fw={'bold'} fs={'2rem'}>
                  {item.name}
                </Wrap>
                <Wrap>
                  <Wrap>
                    <Button
                      icon
                      transparent
                      onClick={(e: any) => {
                        e.preventDefault();
                        setActiveTestType(item);
                        setShowAddTestType(true);
                      }}
                    >
                      <EditIcon color={accentColorPrim} width={'2rem'} />
                    </Button>
                  </Wrap>
                  <Wrap margin={'0 0 0 1rem'} style={{ display: 'none' }}>
                    <Button
                      icon
                      transparent
                      onClick={(e: any) => {
                        e.preventDefault();
                        removeTestType(item.id);
                      }}
                    >
                      <DeleteIcon color={red} width={'2rem'} />
                    </Button>
                  </Wrap>
                </Wrap>
              </Wrap>
              <Wrap color={fontColorSecond} margin={'2rem 0 0 0'}>
                {item.id}
              </Wrap>
            </Wrap>
          ))}
        </Wrap>
      </Wrap>
      {showAddTestType && (
        <DialogWrap
          title={intl.formatMessage(
            activeTestType?.id ? messages.editTestType : messages.addTestType,
          )}
          children={
            <TestTypeDetails
              onSave={addTestType}
              onCancel={() => {
                setShowAddTestType(false);
                setActiveTestType(undefined);
              }}
              testTypeName={activeTestType?.name}
            />
          }
        />
      )}
      {showConfirmation && confirmationHandler && (
        <ConfirmationDialog
          bodyText={confirmationText}
          handleCancel={() => setShowConfirmation(false)}
          handleConfirm={confirmationHandler}
        />
      )}
    </Wrap>
  );
};

export default connector(TestTypes);
